import { useRef, useState, type KeyboardEvent } from "react";

type FilterChipInputProps = {
  label: string;
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  hint?: string;
  className?: string;
};

const commitTokens = (raw: string, existing: string[]) => {
  const tokens = String(raw || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (!tokens.length) return existing;
  const next = [...existing];
  for (const token of tokens) {
    if (!next.some((v) => v.toLowerCase() === token.toLowerCase())) {
      next.push(token);
    }
  }
  return next;
};

export default function FilterChipInput({
  label,
  values,
  onChange,
  placeholder = "Type and press Enter",
  hint = "Press Enter or comma to add. Remove a tag with its × only.",
  className = "",
}: FilterChipInputProps) {
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const valuesRef = useRef(values);
  valuesRef.current = values;

  const addFromDraft = () => {
    const text = draft.trim();
    if (!text) {
      setDraft("");
      return;
    }
    onChange(commitTokens(text, valuesRef.current || []));
    setDraft("");
  };

  const removeAt = (index: number) => {
    const current = valuesRef.current || [];
    onChange(current.filter((_, i) => i !== index));
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      addFromDraft();
    }
    // Backspace must NOT remove tags — only the × button does.
  };

  return (
    <div className={`block min-w-0 max-w-full text-xs font-semibold text-dark-grey ${className}`}>
      <p className="mb-0">{label}</p>
      <div
        className="mt-1 flex min-h-[42px] w-full min-w-0 cursor-text flex-wrap items-center gap-1.5 rounded-md border border-light-grey bg-white px-2 py-1.5 focus-within:border-mainColor"
        onMouseDown={(e) => {
          // Focus the text input when clicking empty padding, not when clicking ×.
          if ((e.target as HTMLElement).closest("[data-chip-remove]")) return;
          if (e.target !== inputRef.current) {
            e.preventDefault();
            inputRef.current?.focus();
          }
        }}
      >
        {(values || []).map((value, index) => (
          <span
            key={`${value}__${index}`}
            className="inline-flex max-w-full items-center gap-1 rounded-full border border-mainColor/20 bg-primary/50 px-2 py-0.5 text-[11px] font-medium text-dark-black"
          >
            <span className="truncate">{value}</span>
            <button
              type="button"
              data-chip-remove
              aria-label={`Remove ${value}`}
              className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-dark-grey hover:bg-white/80 hover:text-dark-black"
              onMouseDown={(e) => {
                // Prevent input blur/focus races that can feel like random deletes.
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                removeAt(index);
              }}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => {
            if (draft.trim()) addFromDraft();
          }}
          placeholder={(values || []).length ? "Add another…" : placeholder}
          className="min-w-0 max-w-full flex-1 basis-[7rem] border-0 bg-transparent py-1 text-sm font-normal text-dark-black outline-none placeholder:text-dark-grey"
        />
      </div>
      {hint ? <p className="mt-1 text-[10px] font-normal text-dark-grey">{hint}</p> : null}
    </div>
  );
}
